'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSignMessage } from 'wagmi'
import { z } from 'zod'
import { useAccount } from './useAccount'

const WalletSessionStatusSchema = z.object({
  authenticated: z.boolean(),
  address: z.string().nullable(),
})

const WalletSessionChallengeSchema = z.object({
  message: z.string().min(1),
  expiresAt: z.string().datetime(),
})

const WalletSessionVerificationSchema = WalletSessionStatusSchema.extend({
  expiresAt: z.string().datetime().optional(),
})

export const walletSessionQueryKey = ['wallet-auth-session'] as const

async function parseResponse<T>(response: Response, schema: z.ZodSchema<T>, fallbackError: string): Promise<T> {
  const json = await response.json().catch(() => null)

  if (!response.ok) {
    const errorMessage =
      json && typeof json === 'object' && 'error' in json && typeof json.error === 'string' ? json.error : fallbackError
    throw new Error(errorMessage)
  }

  return schema.parse(json)
}

export function useWalletAuthSession() {
  const address = useAccount()
  const queryClient = useQueryClient()
  const { signMessageAsync } = useSignMessage()

  const sessionQuery = useQuery({
    queryKey: walletSessionQueryKey,
    queryFn: async () => {
      const response = await fetch('/api/auth/wallet-session', {
        method: 'GET',
        cache: 'no-store',
      })

      return parseResponse(response, WalletSessionStatusSchema, 'Failed to load wallet session')
    },
    staleTime: 0,
    refetchInterval: false,
  })

  const ensureSessionMutation = useMutation({
    mutationFn: async () => {
      if (!address) {
        throw new Error('Connect wallet first')
      }

      const challengeResponse = await fetch('/api/auth/wallet-session/challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address }),
      })

      const challenge = await parseResponse(
        challengeResponse,
        WalletSessionChallengeSchema,
        'Failed to create wallet auth challenge',
      )
      const signature = await signMessageAsync({ message: challenge.message })

      const verificationResponse = await fetch('/api/auth/wallet-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address,
          message: challenge.message,
          signature,
        }),
      })

      return parseResponse(
        verificationResponse,
        WalletSessionVerificationSchema,
        'Failed to verify wallet session',
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: walletSessionQueryKey })
      await queryClient.invalidateQueries({ queryKey: ['flags'] })
    },
  })

  const clearSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/auth/wallet-session', {
        method: 'DELETE',
      })

      return parseResponse(response, WalletSessionStatusSchema, 'Failed to clear wallet session')
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: walletSessionQueryKey })
      await queryClient.invalidateQueries({ queryKey: ['flags'] })
    },
  })

  const connectedAddress = address?.toLowerCase() ?? null
  const sessionAddress = sessionQuery.data?.address?.toLowerCase() ?? null
  const hasMatchingSession = Boolean(connectedAddress && sessionQuery.data?.authenticated && sessionAddress === connectedAddress)
  const {
    isPending: isClearingSession,
    isError: isClearSessionError,
    mutate: clearSessionMutate,
    mutateAsync: clearSession,
    reset: resetClearSession,
  } = clearSessionMutation

  // Reset the clear-session error when the wallet changes so a new switch can retry
  // NOTE: isClearSessionError is intentionally NOT in the dependency array to avoid
  // an infinite retry loop (reset → re-trigger cleanup → fail → reset → ...)
  useEffect(() => {
    resetClearSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedAddress])

  useEffect(() => {
    if (sessionQuery.isLoading || isClearingSession || isClearSessionError) return

    if (!connectedAddress && sessionQuery.data?.authenticated) {
      clearSessionMutate()
      return
    }

    if (connectedAddress && sessionQuery.data?.authenticated && sessionAddress && sessionAddress !== connectedAddress) {
      clearSessionMutate()
    }
  }, [
    connectedAddress,
    clearSessionMutate,
    isClearingSession,
    isClearSessionError,
    sessionAddress,
    sessionQuery.data?.authenticated,
    sessionQuery.isLoading,
  ])

  return {
    address,
    session: sessionQuery.data,
    sessionAddress,
    hasMatchingSession,
    ensureSession: ensureSessionMutation.mutateAsync,
    clearSession,
    isLoadingSession: sessionQuery.isLoading,
    isRefreshingSession: sessionQuery.isFetching,
    isAuthenticating: ensureSessionMutation.isPending,
    isClearingSession,
    error: ensureSessionMutation.error ?? clearSessionMutation.error ?? sessionQuery.error ?? null,
  }
}
